import * as React from "react";

import { SessionContext } from "@mne-authentication/session2";
import { useNavigate } from "react-router";

import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Button from "@mui/material/Button";
import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { FormattedMessage, useIntl } from "@mnemonic/intl";
import { Section } from "@mnemonic/mui";

import { commonMessages } from "../commonMessages";
import { PasswordChecker } from "./PasswordChecker";

export interface IChangePasswordProps {
  showCancelButton?: boolean;
  loading?: boolean;
  newPassword: string;
  errorMessages: {
    currentPasswordField: string;
    newPasswordField: string;
    repeatNewPasswordField: string;
  };
  onSubmit: () => void;
  onChange(value: string);
}

const ChangePasswordForm = ({
  errorMessages,
  loading,
  newPassword,
  onChange,
  onSubmit,
  showCancelButton,
}: IChangePasswordProps) => {
  const navigate = useNavigate();
  const { session } = React.useContext(SessionContext);
  const { formatMessage } = useIntl();
  const [showPassword, setShowPassword] = React.useState({ current: false, new: false, repeat: false });

  const endAdornment = (fieldId) => ({
    endAdornment: (
      <InputAdornment position="end">
        <IconButton
          onClick={() => setShowPassword({ ...showPassword, [fieldId]: !showPassword[fieldId] })}
          edge="end"
          aria-label="toggle password visibility"
        >
          {showPassword[fieldId] ? <VisibilityOff /> : <Visibility />}
        </IconButton>
      </InputAdornment>
    ),
  });

  return (
    <Section loading={loading} sx={{ p: 2, maxWidth: "75%", m: "auto" }}>
      <Section.Header title={formatMessage(commonMessages.ChangePassword)} />
      <Section.Content>
        <form>
          <Grid container spacing={2}>
            <Grid
              size={{
                xs: 12,
                sm: 12,
              }}
            >
              <TextField
                id="current-password-field"
                error={errorMessages.currentPasswordField !== null}
                fullWidth
                label={<FormattedMessage id="change.password.current.field" defaultMessage="Current password" />}
                onChange={onChange("currentPassword")}
                type={showPassword.current ? "text" : "password"}
                slotProps={{
                  input: endAdornment("current"),
                }}
              />
              {errorMessages.currentPasswordField && (
                <FormHelperText error>{errorMessages.currentPasswordField}</FormHelperText>
              )}
            </Grid>

            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <TextField
                error={errorMessages.newPasswordField !== null}
                fullWidth
                id="new-password-field"
                label={<FormattedMessage id="change.password.new" defaultMessage="New password" />}
                onChange={onChange("newPassword")}
                type={showPassword.new ? "text" : "password"}
                variant="outlined"
                slotProps={{
                  input: endAdornment("new"),
                }}
              />
              {errorMessages.newPasswordField && (
                <FormHelperText error>{errorMessages.newPasswordField}</FormHelperText>
              )}
            </Grid>

            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <TextField
                id="repeat-password-field"
                variant="outlined"
                type={showPassword.repeat ? "text" : "password"}
                label={<FormattedMessage id="change.password.repeat" defaultMessage="Repeat new password" />}
                onChange={onChange("repeatNewPassword")}
                error={errorMessages.repeatNewPasswordField !== null}
                fullWidth
                slotProps={{
                  input: endAdornment("repeat"),
                }}
              />
              {errorMessages.repeatNewPasswordField && (
                <FormHelperText error>{errorMessages.repeatNewPasswordField}</FormHelperText>
              )}
            </Grid>
            <Grid sx={{ my: 1 }} size={12}>
              <Typography variant="body1">
                <FormattedMessage id="change.password.requirements" defaultMessage="Password requirements:" />
              </Typography>
              <PasswordChecker newPassword={newPassword} sessionKey={session?.info?.sessionKey} />
            </Grid>

            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <Button
                id="change-password-button"
                disabled={loading}
                size="large"
                color="secondary"
                variant="contained"
                onClick={() => onSubmit()}
              >
                <FormattedMessage id="change.password.save" defaultMessage="Save" />
              </Button>
              {showCancelButton && (
                <Button id="cancel-button" size="large" color="primary" onClick={() => navigate(-1)}>
                  <FormattedMessage id="Cancel" />
                </Button>
              )}
            </Grid>
          </Grid>
        </form>
      </Section.Content>
    </Section>
  );
};
export default ChangePasswordForm;
